import { $modelRelationships, $coreImplementation } from "@gadgetinc/core";
import { pipe, map } from "wonka";
import { GadgetConnection } from "./connection/GadgetConnection.js";
import { InternalModelManager } from "./connection/InternalModelManager.js";
import { AuthenticationMode } from "./connection/ClientOptions.js";
import { assert, GadgetClientError, disambiguateActionVariables, disambiguateBulkActionVariables, capitalizeIdentifier, namespaceDataPath, camelize, isEqual } from "./connection/support.js";
import { ErrorWrapper } from "./connection/ErrorWrapper.js";
import { GadgetRecord } from "./connection/GadgetRecord.js";
import { enqueueActionRunner } from "./connection/operationRunners.js";
import { BackgroundActionHandle } from "./connection/BackgroundActionHandle.js";
;
import { buildGlobalAction, buildComputedView, buildInlineComputedView } from "./builder.js";
import { PlanManager } from "./models/Plan.js";
import { ShopifyShopManager } from "./models/ShopifyShop.js";
import { ShopifySyncManager } from "./models/ShopifySync.js";
import { SessionManager } from "./models/Session.js";
import { CurrentSessionManager } from "./models/CurrentSession.js";
import { RulesTemplateManager } from "./models/RulesTemplate.js";
import { CampaignRulesManager } from "./models/CampaignRules.js";
import { ShopifyCustomerManager } from "./models/ShopifyCustomer.js";
import { ShopifyCustomerAddressManager } from "./models/ShopifyCustomerAddress.js";
import { ShopifyDiscountCodeManager } from "./models/ShopifyDiscountCode.js";
import { ShopifyPriceRuleManager } from "./models/ShopifyPriceRule.js";
import { ShopifyProductManager } from "./models/ShopifyProduct.js";
import { EmailTemplateManager } from "./models/EmailTemplate.js";
import { CampaignEmailSettingsManager } from "./models/CampaignEmailSettings.js";
import { EmailProviderConfigurationManager } from "./models/EmailProviderConfiguration.js";
import { CampaignEventsManager } from "./models/CampaignEvents.js";
import { SurveyResponseManager } from "./models/SurveyResponse.js";
import { ExperimentVariantManager } from "./models/ExperimentVariant.js";
import { ExperimentResultManager } from "./models/ExperimentResult.js";
import { ExperimentSegmentManager } from "./models/ExperimentSegment.js";
import { ExperimentAnalyticsManager } from "./models/ExperimentAnalytics.js";
import { ExperimentManager } from "./models/Experiment.js";
import { ExperimentAssignmentManager } from "./models/ExperimentAssignment.js";
import { CampaignManager } from "./models/Campaign.js";
import { ShopifyAppSubscriptionManager } from "./models/ShopifyAppSubscription.js";
import { ShopifyFileManager } from "./models/ShopifyFile.js";
import { ShopifyProductMediaManager } from "./models/ShopifyProductMedia.js";
import { ShopifyGdprRequestManager } from "./models/ShopifyGdprRequest.js";
import { GdprExportManager } from "./models/GdprExport.js";
import { ShopNotificationsManager } from "./models/ShopNotifications.js";
import { AnalyticsConfigurationManager } from "./models/AnalyticsConfiguration.js";
import { HolidayCacheManager } from "./models/HolidayCache.js";
import { CampaignTemplateManager } from "./models/CampaignTemplate.js";
import { CustomThemeManager } from "./models/CustomTheme.js";
import { TemplateAssetManager } from "./models/TemplateAsset.js";
import { EmailCaptureManager } from "./models/EmailCapture.js";
import { SpinwheelResultManager } from "./models/SpinwheelResult.js";
import { AiInsightManager } from "./models/AiInsight.js";
import { IntentWeightConfigManager } from "./models/IntentWeightConfig.js";
import { AiUsageDailyManager } from "./models/AiUsageDaily.js";
import { EmailLogManager } from "./models/EmailLog.js";
import { ShopifyOrderManager } from "./models/ShopifyOrder.js";
import { ShopifyAppManager } from "./models/ShopifyApp.js";
import { TargetingRuleManager } from "./models/TargetingRule.js";
import { KlaviyoAudienceMembershipManager } from "./models/KlaviyoAudienceMembership.js";
import { ShopPropertyKeyManager } from "./models/ShopPropertyKey.js";
import { PartnerReferralManager } from "./models/PartnerReferral.js";
import { DefaultPlanSelection as DefaultPlanSelection2 } from "./models/Plan.js";
import { DefaultShopifyShopSelection as DefaultShopifyShopSelection2 } from "./models/ShopifyShop.js";
import { DefaultShopifySyncSelection as DefaultShopifySyncSelection2 } from "./models/ShopifySync.js";
import { DefaultSessionSelection as DefaultSessionSelection2 } from "./models/Session.js";
import { DefaultRulesTemplateSelection as DefaultRulesTemplateSelection2 } from "./models/RulesTemplate.js";
import { DefaultCampaignRulesSelection as DefaultCampaignRulesSelection2 } from "./models/CampaignRules.js";
import { DefaultShopifyCustomerSelection as DefaultShopifyCustomerSelection2 } from "./models/ShopifyCustomer.js";
import { DefaultShopifyCustomerAddressSelection as DefaultShopifyCustomerAddressSelection2 } from "./models/ShopifyCustomerAddress.js";
import { DefaultShopifyDiscountCodeSelection as DefaultShopifyDiscountCodeSelection2 } from "./models/ShopifyDiscountCode.js";
import { DefaultShopifyPriceRuleSelection as DefaultShopifyPriceRuleSelection2 } from "./models/ShopifyPriceRule.js";
import { DefaultShopifyProductSelection as DefaultShopifyProductSelection2 } from "./models/ShopifyProduct.js";
import { DefaultEmailTemplateSelection as DefaultEmailTemplateSelection2 } from "./models/EmailTemplate.js";
import { DefaultCampaignEmailSettingsSelection as DefaultCampaignEmailSettingsSelection2 } from "./models/CampaignEmailSettings.js";
import { DefaultEmailProviderConfigurationSelection as DefaultEmailProviderConfigurationSelection2 } from "./models/EmailProviderConfiguration.js";
import { DefaultCampaignEventsSelection as DefaultCampaignEventsSelection2 } from "./models/CampaignEvents.js";
import { DefaultSurveyResponseSelection as DefaultSurveyResponseSelection2 } from "./models/SurveyResponse.js";
import { DefaultExperimentVariantSelection as DefaultExperimentVariantSelection2 } from "./models/ExperimentVariant.js";
import { DefaultExperimentResultSelection as DefaultExperimentResultSelection2 } from "./models/ExperimentResult.js";
import { DefaultExperimentSegmentSelection as DefaultExperimentSegmentSelection2 } from "./models/ExperimentSegment.js";
import { DefaultExperimentAnalyticsSelection as DefaultExperimentAnalyticsSelection2 } from "./models/ExperimentAnalytics.js";
import { DefaultExperimentSelection as DefaultExperimentSelection2 } from "./models/Experiment.js";
import { DefaultExperimentAssignmentSelection as DefaultExperimentAssignmentSelection2 } from "./models/ExperimentAssignment.js";
import { DefaultCampaignSelection as DefaultCampaignSelection2 } from "./models/Campaign.js";
import { DefaultShopifyAppSubscriptionSelection as DefaultShopifyAppSubscriptionSelection2 } from "./models/ShopifyAppSubscription.js";
import { DefaultShopifyFileSelection as DefaultShopifyFileSelection2 } from "./models/ShopifyFile.js";
import { DefaultShopifyProductMediaSelection as DefaultShopifyProductMediaSelection2 } from "./models/ShopifyProductMedia.js";
import { DefaultShopifyGdprRequestSelection as DefaultShopifyGdprRequestSelection2 } from "./models/ShopifyGdprRequest.js";
import { DefaultGdprExportSelection as DefaultGdprExportSelection2 } from "./models/GdprExport.js";
import { DefaultShopNotificationsSelection as DefaultShopNotificationsSelection2 } from "./models/ShopNotifications.js";
import { DefaultAnalyticsConfigurationSelection as DefaultAnalyticsConfigurationSelection2 } from "./models/AnalyticsConfiguration.js";
import { DefaultHolidayCacheSelection as DefaultHolidayCacheSelection2 } from "./models/HolidayCache.js";
import { DefaultCampaignTemplateSelection as DefaultCampaignTemplateSelection2 } from "./models/CampaignTemplate.js";
import { DefaultCustomThemeSelection as DefaultCustomThemeSelection2 } from "./models/CustomTheme.js";
import { DefaultTemplateAssetSelection as DefaultTemplateAssetSelection2 } from "./models/TemplateAsset.js";
import { DefaultEmailCaptureSelection as DefaultEmailCaptureSelection2 } from "./models/EmailCapture.js";
import { DefaultSpinwheelResultSelection as DefaultSpinwheelResultSelection2 } from "./models/SpinwheelResult.js";
import { DefaultAiInsightSelection as DefaultAiInsightSelection2 } from "./models/AiInsight.js";
import { DefaultIntentWeightConfigSelection as DefaultIntentWeightConfigSelection2 } from "./models/IntentWeightConfig.js";
import { DefaultAiUsageDailySelection as DefaultAiUsageDailySelection2 } from "./models/AiUsageDaily.js";
import { DefaultEmailLogSelection as DefaultEmailLogSelection2 } from "./models/EmailLog.js";
import { DefaultShopifyOrderSelection as DefaultShopifyOrderSelection2 } from "./models/ShopifyOrder.js";
import { DefaultShopifyAppSelection as DefaultShopifyAppSelection2 } from "./models/ShopifyApp.js";
import { DefaultTargetingRuleSelection as DefaultTargetingRuleSelection2 } from "./models/TargetingRule.js";
import { DefaultKlaviyoAudienceMembershipSelection as DefaultKlaviyoAudienceMembershipSelection2 } from "./models/KlaviyoAudienceMembership.js";
import { DefaultShopPropertyKeySelection as DefaultShopPropertyKeySelection2 } from "./models/ShopPropertyKey.js";
import { DefaultPartnerReferralSelection as DefaultPartnerReferralSelection2 } from "./models/PartnerReferral.js";
const productionEnv = "production";
const fallbackEnv = "development";
const availableAuthenticationModes = [
  "apiKey",
  "browserSession",
  "anonymous",
  "internalAuthToken",
  "internal",
  "custom"
];
const maybeGetAuthenticationModeOptionsFromClientOptions = (options) => {
  const topLevelAuthModes = {};
  for (const key of availableAuthenticationModes) {
    if (key in options) {
      topLevelAuthModes[key] = options[key];
    }
  }
  if ("authenticationMode" in options && Object.keys(topLevelAuthModes).length > 0) {
    throw new GadgetClientError(
      "Declaring authentication modes at the top level and under the `authenticationMode` key at the same time is not allowed."
    );
  }
  if ("authenticationMode" in options) {
    return options.authenticationMode;
  }
  if (Object.keys(topLevelAuthModes).length === 0) {
    return void 0;
  }
  return topLevelAuthModes;
};
const getImplicitEnv = () => {
  try {
    return process.env.GADGET_ENV;
  } catch (error) {
    return void 0;
  }
};
class NudgesmartClient {
  constructor(options) {
    this.$args = Symbol.for("gadget/fieldArgs");
    /** @deprecated The action aiCostAlerts does not have an api trigger and cannot be called from this api client. If you are the developer of this application and want api clients to call this action add an api trigger to the action. For more information see: https://docs.gadget.dev/guides/actions/triggers */
    this.aiCostAlerts = buildGlobalAction(this, {
      type: "stubbedAction",
      functionName: "aiCostAlerts",
      actionApiIdentifier: "aiCostAlerts",
      dataPath: "aiCostAlerts",
      errorMessage: "The action aiCostAlerts does not have an api trigger and cannot be called from this api client. If you are the developer of this application and want api clients to call this action add an api trigger to the action. For more information see: https://docs.gadget.dev/guides/actions/triggers",
      variables: {},
      reason: "MissingApiTrigger"
    });
    /** Executes the applyGlobalPriorToAllShops global action. */
    this.applyGlobalPriorToAllShops = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "applyGlobalPriorToAllShops",
      operationName: "applyGlobalPriorToAllShops",
      operationReturnType: "ApplyGlobalPriorToAllShops",
      namespace: null,
      variables: {}
    });
    /** Executes the applyTargetingRuleToCampaigns global action. */
    this.applyTargetingRuleToCampaigns = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "applyTargetingRuleToCampaigns",
      operationName: "applyTargetingRuleToCampaigns",
      operationReturnType: "ApplyTargetingRuleToCampaigns",
      namespace: null,
      variables: {
        targetingRuleId: { required: false, type: "String" },
        campaignIds: { required: false, type: "String" },
        convertSmartCampaigns: { required: false, type: "Boolean" }
      }
    });
    /** Executes the checkCampaignExists global action. */
    this.checkCampaignExists = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "checkCampaignExists",
      operationName: "checkCampaignExists",
      operationReturnType: "CheckCampaignExists",
      namespace: null,
      variables: { shopId: { required: false, type: "String" } }
    });
    /** Executes the checkCampaignNoImpressions global action. */
    this.checkCampaignNoImpressions = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "checkCampaignNoImpressions",
      operationName: "checkCampaignNoImpressions",
      operationReturnType: "CheckCampaignNoImpressions",
      namespace: null,
      variables: {}
    });
    /** Executes the cleanupAiCampaignArtifacts global action. */
    this.cleanupAiCampaignArtifacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "cleanupAiCampaignArtifacts",
      operationName: "cleanupAiCampaignArtifacts",
      operationReturnType: "CleanupAiCampaignArtifacts",
      namespace: null,
      variables: {}
    });
    /** Executes the computeIntentWeightsForShop global action. */
    this.computeIntentWeightsForShop = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "computeIntentWeightsForShop",
      operationName: "computeIntentWeightsForShop",
      operationReturnType: "ComputeIntentWeightsForShop",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        days: { required: false, type: "Float" },
        batchId: { required: false, type: "String" }
      }
    });
    /** Executes the computeShopIntentWeights global action. */
    this.computeShopIntentWeights = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "computeShopIntentWeights",
      operationName: "computeShopIntentWeights",
      operationReturnType: "ComputeShopIntentWeights",
      namespace: null,
      variables: {}
    });
    /** Executes the dailyMaintenanceCleanup global action. */
    this.dailyMaintenanceCleanup = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "dailyMaintenanceCleanup",
      operationName: "dailyMaintenanceCleanup",
      operationReturnType: "DailyMaintenanceCleanup",
      namespace: null,
      variables: {}
    });
    /** Executes the duplicateTargetingRule global action. */
    this.duplicateTargetingRule = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "duplicateTargetingRule",
      operationName: "duplicateTargetingRule",
      operationReturnType: "DuplicateTargetingRule",
      namespace: null,
      variables: { targetingRuleId: { required: false, type: "String" } }
    });
    /** Executes the generateAiInsightsDispatcher global action. */
    this.generateAiInsightsDispatcher = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "generateAiInsightsDispatcher",
      operationName: "generateAiInsightsDispatcher",
      operationReturnType: "GenerateAiInsightsDispatcher",
      namespace: null,
      variables: {}
    });
    /** Executes the generateAiInsightsForShops global action. */
    this.generateAiInsightsForShops = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "generateAiInsightsForShops",
      operationName: "generateAiInsightsForShops",
      operationReturnType: "GenerateAiInsightsForShops",
      namespace: null,
      variables: {
        shopIds: { required: false, type: "String" },
        batchId: { required: false, type: "String" }
      }
    });
    /** Executes the getHandle global action. */
    this.getHandle = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getHandle",
      operationName: "getHandle",
      operationReturnType: "GetHandle",
      namespace: null,
      variables: {}
    });
    /** Executes the getSmartTriggerStats global action. */
    this.getSmartTriggerStats = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getSmartTriggerStats",
      operationName: "getSmartTriggerStats",
      operationReturnType: "GetSmartTriggerStats",
      namespace: null,
      variables: {
        campaignId: { required: false, type: "String" },
        days: { required: false, type: "Float" }
      }
    });
    /** Executes the migrateInlineCampaignImages global action. */
    this.migrateInlineCampaignImages = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "migrateInlineCampaignImages",
      operationName: "migrateInlineCampaignImages",
      operationReturnType: "MigrateInlineCampaignImages",
      namespace: null,
      variables: {
        apply: { required: false, type: "Boolean" },
        sync: { required: false, type: "Boolean" },
        republish: { required: false, type: "Boolean" },
        campaignId: { required: false, type: "String" },
        shopId: { required: false, type: "String" },
        limit: { required: false, type: "Float" },
        pageSize: { required: false, type: "Float" }
      }
    });
    /** Executes the notifyOpsEvent global action. */
    this.notifyOpsEvent = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "notifyOpsEvent",
      operationName: "notifyOpsEvent",
      operationReturnType: "NotifyOpsEvent",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        event: { required: false, type: "String" },
        detail: { required: false, type: "JSONObject" }
      }
    });
    /** Executes the opsResetEmailDedup global action. */
    this.opsResetEmailDedup = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "opsResetEmailDedup",
      operationName: "opsResetEmailDedup",
      operationReturnType: "OpsResetEmailDedup",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        template: { required: false, type: "String" }
      }
    });
    /** Executes the opsRunDrips global action. */
    this.opsRunDrips = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "opsRunDrips",
      operationName: "opsRunDrips",
      operationReturnType: "OpsRunDrips",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        force: { required: false, type: "Boolean" }
      }
    });
    /** Executes the opsSendBroadcast global action. */
    this.opsSendBroadcast = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "opsSendBroadcast",
      operationName: "opsSendBroadcast",
      operationReturnType: "OpsSendBroadcast",
      namespace: null,
      variables: {
        broadcastKey: { required: false, type: "String" },
        headline: { required: false, type: "String" },
        body: { required: false, type: "String" },
        ctaText: { required: false, type: "String" },
        ctaUrl: { required: false, type: "String" },
        mode: { required: false, type: "String" },
        testEmail: { required: false, type: "String" },
        acknowledgeReviewCopy: { required: false, type: "Boolean" }
      }
    });
    /** Executes the opsSendSegment global action. */
    this.opsSendSegment = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "opsSendSegment",
      operationName: "opsSendSegment",
      operationReturnType: "OpsSendSegment",
      namespace: null,
      variables: {
        shopIds: { required: false, type: "String" },
        broadcastKey: { required: false, type: "String" },
        headline: { required: false, type: "String" },
        body: { required: false, type: "String" },
        ctaText: { required: false, type: "String" },
        ctaUrl: { required: false, type: "String" },
        mode: { required: false, type: "String" },
        testEmail: { required: false, type: "String" },
        acknowledgeReviewCopy: { required: false, type: "Boolean" }
      }
    });
    /** Executes the opsSendTestEmail global action. */
    this.opsSendTestEmail = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "opsSendTestEmail",
      operationName: "opsSendTestEmail",
      operationReturnType: "OpsSendTestEmail",
      namespace: null,
      variables: {
        template: { required: false, type: "String" },
        mode: { required: false, type: "String" },
        to: { required: false, type: "String" },
        props: { required: false, type: "JSONObject" }
      }
    });
    /** Executes the processGdprRedaction global action. */
    this.processGdprRedaction = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processGdprRedaction",
      operationName: "processGdprRedaction",
      operationReturnType: "ProcessGdprRedaction",
      namespace: null,
      variables: {
        requestId: { required: false, type: "String" },
        shopId: { required: false, type: "String" },
        exportId: { required: false, type: "String" },
        modelIndex: { required: false, type: "Float" },
        lastId: { required: false, type: "String" },
        cdnDone: { required: false, type: "Boolean" }
      }
    });
    /** Executes the processMerchantEmailDrips global action. */
    this.processMerchantEmailDrips = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processMerchantEmailDrips",
      operationName: "processMerchantEmailDrips",
      operationReturnType: "ProcessMerchantEmailDrips",
      namespace: null,
      variables: {}
    });
    /** Executes the pruneKlaviyoAudienceMemberships global action. */
    this.pruneKlaviyoAudienceMemberships = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "pruneKlaviyoAudienceMemberships",
      operationName: "pruneKlaviyoAudienceMemberships",
      operationReturnType: "PruneKlaviyoAudienceMemberships",
      namespace: null,
      variables: {}
    });
    /** Executes the publishCampaignPayload global action. */
    this.publishCampaignPayload = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "publishCampaignPayload",
      operationName: "publishCampaignPayload",
      operationReturnType: "PublishCampaignPayload",
      namespace: null,
      variables: { campaignId: { required: false, type: "String" } }
    });
    /** Executes the reapOrphanedPayloads global action. */
    this.reapOrphanedPayloads = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "reapOrphanedPayloads",
      operationName: "reapOrphanedPayloads",
      operationReturnType: "ReapOrphanedPayloads",
      namespace: null,
      variables: {}
    });
    /** Executes the reapPendingTemplateAssets global action. */
    this.reapPendingTemplateAssets = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "reapPendingTemplateAssets",
      operationName: "reapPendingTemplateAssets",
      operationReturnType: "ReapPendingTemplateAssets",
      namespace: null,
      variables: {}
    });
    /** Executes the reconcileMauticContacts global action. */
    this.reconcileMauticContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "reconcileMauticContacts",
      operationName: "reconcileMauticContacts",
      operationReturnType: "ReconcileMauticContacts",
      namespace: null,
      variables: { force: { required: false, type: "Boolean" } }
    });
    /** Executes the reconcileSubscriptions global action. */
    this.reconcileSubscriptions = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "reconcileSubscriptions",
      operationName: "reconcileSubscriptions",
      operationReturnType: "ReconcileSubscriptions",
      namespace: null,
      variables: { sync: { required: false, type: "Boolean" } }
    });
    /** Executes the runShopifySyncForShops global action. */
    this.runShopifySyncForShops = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "runShopifySyncForShops",
      operationName: "runShopifySyncForShops",
      operationReturnType: "RunShopifySyncForShops",
      namespace: null,
      variables: {
        shopIds: { required: false, type: "String" },
        batchId: { required: false, type: "String" }
      }
    });
    /** @deprecated The action scheduledShopifySync does not have an api trigger and cannot be called from this api client. If you are the developer of this application and want api clients to call this action add an api trigger to the action. For more information see: https://docs.gadget.dev/guides/actions/triggers */
    this.scheduledShopifySync = buildGlobalAction(this, {
      type: "stubbedAction",
      functionName: "scheduledShopifySync",
      actionApiIdentifier: "scheduledShopifySync",
      dataPath: "scheduledShopifySync",
      errorMessage: "The action scheduledShopifySync does not have an api trigger and cannot be called from this api client. If you are the developer of this application and want api clients to call this action add an api trigger to the action. For more information see: https://docs.gadget.dev/guides/actions/triggers",
      variables: {},
      reason: "MissingApiTrigger"
    });
    /** Executes the seedIntentWeightConfig global action. */
    this.seedIntentWeightConfig = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "seedIntentWeightConfig",
      operationName: "seedIntentWeightConfig",
      operationReturnType: "SeedIntentWeightConfig",
      namespace: null,
      variables: {}
    });
    /** Executes the seedPlans global action. */
    this.seedPlans = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "seedPlans",
      operationName: "seedPlans",
      operationReturnType: "SeedPlans",
      namespace: null,
      variables: {}
    });
    /** Executes the seedSocialProofTemplates global action. */
    this.seedSocialProofTemplates = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "seedSocialProofTemplates",
      operationName: "seedSocialProofTemplates",
      operationReturnType: "SeedSocialProofTemplates",
      namespace: null,
      variables: {}
    });
    /** Executes the sendBroadcastBatch global action. */
    this.sendBroadcastBatch = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendBroadcastBatch",
      operationName: "sendBroadcastBatch",
      operationReturnType: "SendBroadcastBatch",
      namespace: null,
      variables: {
        shopIds: { required: false, type: "String" },
        broadcastKey: { required: false, type: "String" },
        headline: { required: false, type: "String" },
        body: { required: false, type: "String" },
        ctaText: { required: false, type: "String" },
        ctaUrl: { required: false, type: "String" }
      }
    });
    /** Executes the sendMerchantEventEmail global action. */
    this.sendMerchantEventEmail = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendMerchantEventEmail",
      operationName: "sendMerchantEventEmail",
      operationReturnType: "SendMerchantEventEmail",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        template: { required: false, type: "String" },
        props: { required: false, type: "JSONObject" },
        marketing: { required: false, type: "Boolean" }
      }
    });
    /** Executes the sendMerchantLifecycleEmails global action. */
    this.sendMerchantLifecycleEmails = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendMerchantLifecycleEmails",
      operationName: "sendMerchantLifecycleEmails",
      operationReturnType: "SendMerchantLifecycleEmails",
      namespace: null,
      variables: {
        shopIds: { required: false, type: "String" },
        batchId: { required: false, type: "String" }
      }
    });
    /** Executes the spike43FeatureFunnels global action. */
    this.spike43FeatureFunnels = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "spike43FeatureFunnels",
      operationName: "spike43FeatureFunnels",
      operationReturnType: "Spike43FeatureFunnels",
      namespace: null,
      variables: {
        shopDomain: { required: false, type: "String" },
        days: { required: false, type: "Float" }
      }
    });
    /** Executes the spikeFunnelsAttribution global action. */
    this.spikeFunnelsAttribution = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "spikeFunnelsAttribution",
      operationName: "spikeFunnelsAttribution",
      operationReturnType: "SpikeFunnelsAttribution",
      namespace: null,
      variables: {
        shopDomain: { required: false, type: "String" },
        days: { required: false, type: "Float" }
      }
    });
    /** Executes the spikeSmartTriggerHealth global action. */
    this.spikeSmartTriggerHealth = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "spikeSmartTriggerHealth",
      operationName: "spikeSmartTriggerHealth",
      operationReturnType: "SpikeSmartTriggerHealth",
      namespace: null,
      variables: {
        shopDomain: { required: false, type: "String" },
        days: { required: false, type: "Float" }
      }
    });
    /** Executes the syncMauticContact global action. */
    this.syncMauticContact = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncMauticContact",
      operationName: "syncMauticContact",
      operationReturnType: "SyncMauticContact",
      namespace: null,
      variables: { shopId: { required: false, type: "String" } }
    });
    /** Executes the syncShopSubscription global action. */
    this.syncShopSubscription = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncShopSubscription",
      operationName: "syncShopSubscription",
      operationReturnType: "SyncShopSubscription",
      namespace: null,
      variables: { shopId: { required: false, type: "String" } }
    });
    /** Executes the trackAdminEvent global action. */
    this.trackAdminEvent = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "trackAdminEvent",
      operationName: "trackAdminEvent",
      operationReturnType: "TrackAdminEvent",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        eventName: { required: false, type: "String" },
        eventParams: { required: false, type: "JSONObject" }
      }
    });
    /** Executes the validateIntentScore global action. */
    this.validateIntentScore = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "validateIntentScore",
      operationName: "validateIntentScore",
      operationReturnType: "ValidateIntentScore",
      namespace: null,
      variables: {
        shopDomain: { required: false, type: "String" },
        days: { required: false, type: "Float" }
      }
    });
    /** Executes the writeToShopify global action. */
    this.writeToShopify = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "writeToShopify",
      operationName: "writeToShopify",
      operationReturnType: "WriteToShopify",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        mutation: { required: false, type: "String" },
        variables: { required: false, type: "JSONObject" }
      }
    });
    /** Executes the reportPartnerInstall global action. */
    this.reportPartnerInstall = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "reportPartnerInstall",
      operationName: "reportPartnerInstall",
      operationReturnType: "ReportPartnerInstall",
      namespace: null,
      variables: {
        shop: { required: false, type: "String" },
        ref: { required: false, type: "String" },
        referralId: { required: false, type: "String" },
        mref: { required: false, type: "String" },
        clickToken: { required: false, type: "String" }
      }
    });
    /** Executes the sidekickCampaignEventTotals computed view. */
    this.sidekickCampaignEventTotals = buildComputedView(this, {
      type: "computedView",
      operationName: "sidekickCampaignEventTotals",
      functionName: "sidekickCampaignEventTotals",
      gqlFieldName: "sidekickCampaignEventTotals",
      referencedModelKeys: ["rtB6aXIeODa1"],
      referencedTypenames: ["CampaignEvents"],
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        since: { required: false, type: "DateTime" }
      }
    });
    /** Executes the sidekickCampaignMetrics computed view. */
    this.sidekickCampaignMetrics = buildComputedView(this, {
      type: "computedView",
      operationName: "sidekickCampaignMetrics",
      functionName: "sidekickCampaignMetrics",
      gqlFieldName: "sidekickCampaignMetrics",
      referencedModelKeys: ["rtB6aXIeODa1"],
      referencedTypenames: ["CampaignEvents"],
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        campaignId: { required: false, type: "String" },
        since: { required: false, type: "DateTime" }
      }
    });
    /** Executes an inline computed view. */
    this.view = buildInlineComputedView(this, {
      type: "computedView",
      operationName: "gellyView",
      functionName: "view",
      gqlFieldName: "gellyView",
      namespace: null,
      variables: {
        query: { type: "String", required: true },
        args: { type: "JSONObject" }
      }
    });
    /**
     * The list of environments with a customized API root endpoint
     */
    this.apiRoots = { "development": "https://nudgesmart--development.gadget.app/", "production": "https://app.nudgesmart.com/" };
    this.applicationId = "203291";
    /** Start a transaction against the Gadget backend which will atomically commit (or rollback). */
    this.transaction = async (callback) => {
      return await this.connection.transaction(callback);
    };
    /**
    * Get a new direct upload token for file uploads to directly to cloud storage.
    * See https://docs.gadget.dev/guides/storing-files#direct-uploads-using-tokens for more information
    * @return { url: string, token: string } A `url` to upload one file to, and a token for that file to pass back to Gadget as an action input.
    */
    this.getDirectUploadToken = async () => {
      const result = await this.query("query GetDirectUploadToken($nonce: String) { gadgetMeta { directUploadToken(nonce: $nonce) { url, token } } }", { nonce: Math.random().toString(36).slice(2, 7) }, {
        requestPolicy: "network-only"
      });
      return result.gadgetMeta.directUploadToken;
    };
    let inSSRContext = false;
    this.options = options;
    try {
      inSSRContext = !!(import.meta.env && import.meta.env.SSR);
    } catch (error) {
    }
    if (inSSRContext) {
      const api = globalThis.GadgetGlobals?.api;
      if (api) {
        return api.actAsSession;
      }
    }
    this.environment = (options?.environment ?? getImplicitEnv() ?? fallbackEnv).toLocaleLowerCase();
    let apiRoot;
    if (this.apiRoots[this.environment]) {
      apiRoot = this.apiRoots[this.environment];
    } else {
      const envPart = this.environment == productionEnv ? "" : `--${this.environment}`;
      apiRoot = `https://nudgesmart${envPart}.gadget.app`;
    }
    const exchanges = { ...options?.exchanges };
    if (this.environment !== productionEnv) {
      const devHarnessExchange = ({ forward }) => {
        return (operations$) => {
          const operationResult$ = forward(operations$);
          return pipe(
            operationResult$,
            map((result) => {
              try {
                if (typeof window !== "undefined" && typeof CustomEvent === "function") {
                  const event = new CustomEvent("gadget:devharness:graphqlresult", { detail: result });
                  window.dispatchEvent(event);
                }
              } catch (error) {
                console.warn("[gadget] error dispatching gadget dev harness event", error);
              }
              return result;
            })
          );
        };
      };
      exchanges.beforeAll = [
        devHarnessExchange,
        ...exchanges.beforeAll ?? []
      ];
    }
    const connectionOptions = {
      endpoint: new URL("api/graphql", apiRoot).toString(),
      applicationId: this.applicationId,
      authenticationMode: options?.authenticationMode,
      ...options,
      exchanges,
      environment: this.environment
    };
    const authenticationMode = maybeGetAuthenticationModeOptionsFromClientOptions(options ?? {});
    connectionOptions.authenticationMode = authenticationMode ?? (typeof window == "undefined" ? { anonymous: true } : { browserSession: true });
    this.connection = new GadgetConnection(connectionOptions);
    if (typeof window != "undefined" && typeof window.document != "undefined" && this.connection.authenticationMode == AuthenticationMode.APIKey && !options?.authenticationMode?.dangerouslyAllowBrowserApiKey) {
      throw new Error("GGT_BROWSER_API_KEY_USAGE: Using a Gadget API key to authenticate this client object is insecure and will leak your API keys to attackers. Please use a different authentication mode.");
    }
    if (typeof options?.authenticationMode === "undefined" && typeof window !== "undefined" && window.shopify?.idToken) {
      this.connection.setAuthenticationMode({
        custom: {
          async processFetch(_input, init) {
            const headers = new Headers(init.headers);
            const idToken = await window.shopify.idToken();
            headers.append("Authorization", "ShopifySessionToken " + idToken);
            init.headers ?? (init.headers = {});
            headers.forEach(function(value, key) {
              init.headers[key] = value;
            });
          },
          async processTransactionConnectionParams(params) {
            const idToken = await window.shopify.idToken();
            params.auth.shopifySessionToken = idToken;
          }
        }
      });
    }
    this.plan = new PlanManager(this.connection);
    this.shopifyShop = new ShopifyShopManager(this.connection);
    this.shopifySync = new ShopifySyncManager(this.connection);
    this.session = new SessionManager(this.connection);
    this.currentSession = new CurrentSessionManager(this.connection);
    this.rules_template = new RulesTemplateManager(this.connection);
    this.campaign_rules = new CampaignRulesManager(this.connection);
    this.shopifyCustomer = new ShopifyCustomerManager(this.connection);
    this.shopifyCustomerAddress = new ShopifyCustomerAddressManager(this.connection);
    this.shopifyDiscountCode = new ShopifyDiscountCodeManager(this.connection);
    this.shopifyPriceRule = new ShopifyPriceRuleManager(this.connection);
    this.shopifyProduct = new ShopifyProductManager(this.connection);
    this.email_template = new EmailTemplateManager(this.connection);
    this.campaign_email_settings = new CampaignEmailSettingsManager(this.connection);
    this.emailProviderConfiguration = new EmailProviderConfigurationManager(this.connection);
    this.campaign_events = new CampaignEventsManager(this.connection);
    this.survey_response = new SurveyResponseManager(this.connection);
    this.experiment_variant = new ExperimentVariantManager(this.connection);
    this.experiment_result = new ExperimentResultManager(this.connection);
    this.experiment_segment = new ExperimentSegmentManager(this.connection);
    this.experiment_analytics = new ExperimentAnalyticsManager(this.connection);
    this.experiment = new ExperimentManager(this.connection);
    this.experiment_assignment = new ExperimentAssignmentManager(this.connection);
    this.campaign = new CampaignManager(this.connection);
    this.shopifyAppSubscription = new ShopifyAppSubscriptionManager(this.connection);
    this.shopifyFile = new ShopifyFileManager(this.connection);
    this.shopifyProductMedia = new ShopifyProductMediaManager(this.connection);
    this.shopifyGdprRequest = new ShopifyGdprRequestManager(this.connection);
    this.gdprExport = new GdprExportManager(this.connection);
    this.shop_notifications = new ShopNotificationsManager(this.connection);
    this.analytics_configuration = new AnalyticsConfigurationManager(this.connection);
    this.holidayCache = new HolidayCacheManager(this.connection);
    this.campaign_template = new CampaignTemplateManager(this.connection);
    this.custom_theme = new CustomThemeManager(this.connection);
    this.template_asset = new TemplateAssetManager(this.connection);
    this.email_capture = new EmailCaptureManager(this.connection);
    this.spinwheel_result = new SpinwheelResultManager(this.connection);
    this.ai_insight = new AiInsightManager(this.connection);
    this.intent_weight_config = new IntentWeightConfigManager(this.connection);
    this.aiUsageDaily = new AiUsageDailyManager(this.connection);
    this.emailLog = new EmailLogManager(this.connection);
    this.shopifyOrder = new ShopifyOrderManager(this.connection);
    this.shopifyApp = new ShopifyAppManager(this.connection);
    this.targetingRule = new TargetingRuleManager(this.connection);
    this.klaviyoAudienceMembership = new KlaviyoAudienceMembershipManager(this.connection);
    this.shopPropertyKey = new ShopPropertyKeyManager(this.connection);
    this.partnerReferral = new PartnerReferralManager(this.connection);
    this.internal = {
      plan: new InternalModelManager("plan", this.connection, { "pluralApiIdentifier": "plans", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyShop: new InternalModelManager("shopifyShop", this.connection, { "pluralApiIdentifier": "shopifyShops", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifySync: new InternalModelManager("shopifySync", this.connection, { "pluralApiIdentifier": "shopifySyncs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      session: new InternalModelManager("session", this.connection, { "pluralApiIdentifier": "sessions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      rules_template: new InternalModelManager("rules_template", this.connection, { "pluralApiIdentifier": "rules_templates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      campaign_rules: new InternalModelManager("campaign_rules", this.connection, { "pluralApiIdentifier": "campaign_ruless", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyCustomer: new InternalModelManager("shopifyCustomer", this.connection, { "pluralApiIdentifier": "shopifyCustomers", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyCustomerAddress: new InternalModelManager("shopifyCustomerAddress", this.connection, { "pluralApiIdentifier": "shopifyCustomerAddresses", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyDiscountCode: new InternalModelManager("shopifyDiscountCode", this.connection, { "pluralApiIdentifier": "shopifyDiscountCodes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyPriceRule: new InternalModelManager("shopifyPriceRule", this.connection, { "pluralApiIdentifier": "shopifyPriceRules", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyProduct: new InternalModelManager("shopifyProduct", this.connection, { "pluralApiIdentifier": "shopifyProducts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      email_template: new InternalModelManager("email_template", this.connection, { "pluralApiIdentifier": "email_templates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      campaign_email_settings: new InternalModelManager("campaign_email_settings", this.connection, { "pluralApiIdentifier": "campaign_email_settingss", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      emailProviderConfiguration: new InternalModelManager("emailProviderConfiguration", this.connection, { "pluralApiIdentifier": "emailProviderConfigurations", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      campaign_events: new InternalModelManager("campaign_events", this.connection, { "pluralApiIdentifier": "campaign_eventss", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      survey_response: new InternalModelManager("survey_response", this.connection, { "pluralApiIdentifier": "survey_responses", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      experiment_variant: new InternalModelManager("experiment_variant", this.connection, { "pluralApiIdentifier": "experiment_variants", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      experiment_result: new InternalModelManager("experiment_result", this.connection, { "pluralApiIdentifier": "experiment_results", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      experiment_segment: new InternalModelManager("experiment_segment", this.connection, { "pluralApiIdentifier": "experiment_segments", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      experiment_analytics: new InternalModelManager("experiment_analytics", this.connection, { "pluralApiIdentifier": "experiment_analyticss", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      experiment: new InternalModelManager("experiment", this.connection, { "pluralApiIdentifier": "experiments", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      experiment_assignment: new InternalModelManager("experiment_assignment", this.connection, { "pluralApiIdentifier": "experiment_assignments", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      campaign: new InternalModelManager("campaign", this.connection, { "pluralApiIdentifier": "campaigns", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyAppSubscription: new InternalModelManager("shopifyAppSubscription", this.connection, { "pluralApiIdentifier": "shopifyAppSubscriptions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyFile: new InternalModelManager("shopifyFile", this.connection, { "pluralApiIdentifier": "shopifyFiles", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyProductMedia: new InternalModelManager("shopifyProductMedia", this.connection, { "pluralApiIdentifier": "shopifyProductMedias", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyGdprRequest: new InternalModelManager("shopifyGdprRequest", this.connection, { "pluralApiIdentifier": "shopifyGdprRequests", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      gdprExport: new InternalModelManager("gdprExport", this.connection, { "pluralApiIdentifier": "gdprExports", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shop_notifications: new InternalModelManager("shop_notifications", this.connection, { "pluralApiIdentifier": "shop_notificationss", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      analytics_configuration: new InternalModelManager("analytics_configuration", this.connection, { "pluralApiIdentifier": "analytics_configurations", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      holidayCache: new InternalModelManager("holidayCache", this.connection, { "pluralApiIdentifier": "holidayCaches", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      campaign_template: new InternalModelManager("campaign_template", this.connection, { "pluralApiIdentifier": "campaign_templates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      custom_theme: new InternalModelManager("custom_theme", this.connection, { "pluralApiIdentifier": "custom_themes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      template_asset: new InternalModelManager("template_asset", this.connection, { "pluralApiIdentifier": "template_assets", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      email_capture: new InternalModelManager("email_capture", this.connection, { "pluralApiIdentifier": "email_captures", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      spinwheel_result: new InternalModelManager("spinwheel_result", this.connection, { "pluralApiIdentifier": "spinwheel_results", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      ai_insight: new InternalModelManager("ai_insight", this.connection, { "pluralApiIdentifier": "ai_insights", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      intent_weight_config: new InternalModelManager("intent_weight_config", this.connection, { "pluralApiIdentifier": "intent_weight_configs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      aiUsageDaily: new InternalModelManager("aiUsageDaily", this.connection, { "pluralApiIdentifier": "aiUsageDailies", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      emailLog: new InternalModelManager("emailLog", this.connection, { "pluralApiIdentifier": "emailLogs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyOrder: new InternalModelManager("shopifyOrder", this.connection, { "pluralApiIdentifier": "shopifyOrders", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyApp: new InternalModelManager("shopifyApp", this.connection, { "pluralApiIdentifier": "shopifyApps", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      targetingRule: new InternalModelManager("targetingRule", this.connection, { "pluralApiIdentifier": "targetingRules", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      klaviyoAudienceMembership: new InternalModelManager("klaviyoAudienceMembership", this.connection, { "pluralApiIdentifier": "klaviyoAudienceMemberships", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopPropertyKey: new InternalModelManager("shopPropertyKey", this.connection, { "pluralApiIdentifier": "shopPropertyKeys", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      partnerReferral: new InternalModelManager("partnerReferral", this.connection, { "pluralApiIdentifier": "partnerReferrals", "hasAmbiguousIdentifiers": false, "namespace": [] })
    };
  }
  /**
   * Returns a new Client instance that will call the Gadget API as the application's admin user.
   * This can only be used for API clients using internal authentication.
   * @returns {NudgesmartClient} A new NudgesmartClient instance with admin authentication
   */
  get actAsAdmin() {
    assert(this.options?.authenticationMode?.internal, `actAsAdmin can only be used for API clients using internal authentication, this client is using ${JSON.stringify(this.options?.authenticationMode)}`);
    return new NudgesmartClient({
      ...this.options,
      authenticationMode: {
        internal: {
          ...this.options.authenticationMode.internal,
          actAsSession: false
        }
      }
    });
  }
  /**
   * Returns a new NudgesmartClient instance that will call the Gadget API as with the permission of the current session.
   * This can only be used for API clients using internal authentication.
   * @returns {NudgesmartClient} A new NudgesmartClient instance with session authentication
   */
  get actAsSession() {
    assert(this.options?.authenticationMode?.internal, "actAsSession can only be used for API clients using internal authentication");
    return new NudgesmartClient({
      ...this.options,
      authenticationMode: {
        internal: {
          ...this.options.authenticationMode.internal,
          actAsSession: true
        }
      }
    });
  }
  /** Run an arbitrary GraphQL query. */
  async query(graphQL, variables, options) {
    const { data, error } = await this.connection.currentClient.query(graphQL, variables, options).toPromise();
    if (error)
      throw error;
    return data;
  }
  /** Run an arbitrary GraphQL mutation. */
  async mutate(graphQL, variables, options) {
    const { data, error } = await this.connection.currentClient.mutation(graphQL, variables, options).toPromise();
    if (error)
      throw error;
    return data;
  }
  /**
   * `fetch` function that works the same as the built-in `fetch` function, but automatically passes authentication information for this API client.
   *
   * @example
   * await api.fetch("https://myapp--development.gadget.app/foo/bar");
   *
   * @example
   * // fetch a relative URL from the endpoint this API client is configured to fetch from
   * await api.fetch("/foo/bar");
   **/
  async fetch(input, init = {}) {
    return await this.connection.fetch(input, init);
  }
  async enqueue(action, inputOrOptions, maybeOptions) {
    assert(action, ".enqueue must be passed an action as the first argument but was passed undefined");
    let input;
    let options;
    if (typeof maybeOptions !== "undefined") {
      if (typeof inputOrOptions == "string") {
        input = { id: inputOrOptions };
      } else {
        input = inputOrOptions;
      }
      options = maybeOptions;
    } else if (!action.variables || Object.keys(action.variables).length == 0) {
      input = {};
      options = inputOrOptions;
    } else {
      if (typeof inputOrOptions == "string") {
        input = { id: inputOrOptions };
      } else {
        input = inputOrOptions;
      }
      options = {};
    }
    return await enqueueActionRunner(this.connection, action, input, options);
  }
  /**
   * Returns a handle for a given background action id
   *
   * @param action The action that was enqueued
   * @param id The id of the background action
   *
   * @example
   * const handle = api.handle(api.widget.update, "app-job-12346");
   *
   * @example
   * const handle = api.handle(api.someGlobalAction, "app-job-56789");
   **/
  handle(action, id) {
    return new BackgroundActionHandle(this.connection, action, id);
  }
  toString() {
    return `NudgesmartClient<${this.environment}>`;
  }
  toJSON() {
    return this.toString();
  }
}
NudgesmartClient.prototype[$modelRelationships] = { "plan": {}, "shopifyShop": { "syncs": { "type": "HasMany", "model": "shopifySync" }, "gdprRequests": { "type": "HasMany", "model": "shopifyGdprRequest" }, "campaigns": { "type": "HasMany", "model": "campaign" }, "customerAddresses": { "type": "HasMany", "model": "shopifyCustomerAddress" }, "customers": { "type": "HasMany", "model": "shopifyCustomer" }, "discountCodes": { "type": "HasMany", "model": "shopifyDiscountCode" }, "priceRules": { "type": "HasMany", "model": "shopifyPriceRule" }, "products": { "type": "HasMany", "model": "shopifyProduct" }, "campaignEvents": { "type": "HasMany", "model": "campaign_events" }, "appSubscriptions": { "type": "HasMany", "model": "shopifyAppSubscription" }, "files": { "type": "HasMany", "model": "shopifyFile" }, "orders": { "type": "HasMany", "model": "shopifyOrder" }, "productMedia": { "type": "HasMany", "model": "shopifyProductMedia" } }, "shopifySync": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "session": { "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "campaigns": { "type": "HasMany", "model": "campaign" } }, "rules_template": {}, "campaign_rules": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "targetingRule": { "type": "BelongsTo", "model": "targetingRule" } }, "shopifyCustomer": { "defaultAddress": { "type": "BelongsTo", "model": "shopifyCustomerAddress" }, "addresses": { "type": "HasMany", "model": "shopifyCustomerAddress" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "lastOrder": { "type": "BelongsTo", "model": "shopifyOrder" }, "orders": { "type": "HasMany", "model": "shopifyOrder" } }, "shopifyCustomerAddress": { "shopifyCustomer": { "type": "BelongsTo", "model": "shopifyCustomer" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyDiscountCode": { "priceRule": { "type": "BelongsTo", "model": "shopifyPriceRule" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyPriceRule": { "discountCodes": { "type": "HasMany", "model": "shopifyDiscountCode" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyProduct": { "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "featuredMedia": { "type": "HasOne", "model": "shopifyProductMedia" }, "shopifyProductMedias": { "type": "HasMany", "model": "shopifyProductMedia" }, "media": { "type": "HasManyThrough", "model": "shopifyFile", "through": "shopifyProductMedia" } }, "email_template": {}, "campaign_email_settings": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "emailTemplate": { "type": "BelongsTo", "model": "email_template" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "providerConfiguration": { "type": "BelongsTo", "model": "emailProviderConfiguration" } }, "emailProviderConfiguration": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "campaign_events": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "survey_response": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "experiment_variant": { "assignments": { "type": "HasMany", "model": "experiment_assignment" }, "results": { "type": "HasMany", "model": "experiment_result" }, "analytics": { "type": "HasMany", "model": "experiment_analytics" }, "campaignTemplate": { "type": "BelongsTo", "model": "campaign_template" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "experiment": { "type": "BelongsTo", "model": "experiment" } }, "experiment_result": { "experiment": { "type": "BelongsTo", "model": "experiment" }, "variant": { "type": "BelongsTo", "model": "experiment_variant" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "assignment": { "type": "BelongsTo", "model": "experiment_assignment" } }, "experiment_segment": { "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "createdBy": { "type": "BelongsTo", "model": "session" }, "experiments": { "type": "HasMany", "model": "experiment" } }, "experiment_analytics": { "experiment": { "type": "BelongsTo", "model": "experiment" }, "variant": { "type": "BelongsTo", "model": "experiment_variant" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "experiment": { "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "segment": { "type": "BelongsTo", "model": "experiment_segment" }, "createdBy": { "type": "BelongsTo", "model": "session" }, "assignments": { "type": "HasMany", "model": "experiment_assignment" }, "results": { "type": "HasMany", "model": "experiment_result" }, "analytics": { "type": "HasMany", "model": "experiment_analytics" }, "variants": { "type": "HasMany", "model": "experiment_variant" } }, "experiment_assignment": { "variant": { "type": "BelongsTo", "model": "experiment_variant" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "result": { "type": "HasOne", "model": "experiment_result" }, "experiment": { "type": "BelongsTo", "model": "experiment" } }, "campaign": { "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "campaignEvents": { "type": "HasMany", "model": "campaign_events" }, "campaignTemplate": { "type": "BelongsTo", "model": "campaign_template" }, "createdBy": { "type": "BelongsTo", "model": "session" }, "campaign_rules": { "type": "HasOne", "model": "campaign_rules" }, "updatedBy": { "type": "BelongsTo", "model": "session" }, "templateAssets": { "type": "HasMany", "model": "template_asset" } }, "shopifyAppSubscription": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyFile": { "shop": { "type": "BelongsTo", "model": "shopifyShop" }, "shopifyProductMedias": { "type": "HasMany", "model": "shopifyProductMedia" }, "product": { "type": "HasManyThrough", "model": "shopifyProduct", "through": "shopifyProductMedia" } }, "shopifyProductMedia": { "featuredMediaForProduct": { "type": "BelongsTo", "model": "shopifyProduct" }, "file": { "type": "BelongsTo", "model": "shopifyFile" }, "product": { "type": "BelongsTo", "model": "shopifyProduct" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyGdprRequest": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "gdprExport": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shop_notifications": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "analytics_configuration": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "holidayCache": {}, "campaign_template": { "campaigns": { "type": "HasMany", "model": "campaign" } }, "custom_theme": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "template_asset": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "email_capture": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "spinwheel_result": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "ai_insight": { "campaign": { "type": "BelongsTo", "model": "campaign" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "intent_weight_config": {}, "aiUsageDaily": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "emailLog": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyOrder": { "customer": { "type": "BelongsTo", "model": "shopifyCustomer" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyApp": {}, "targetingRule": { "campaignRules": { "type": "HasMany", "model": "campaign_rules" }, "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "klaviyoAudienceMembership": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopPropertyKey": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "partnerReferral": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } } };
const coreImplementation = {
  GadgetRecord,
  disambiguateActionVariables,
  disambiguateBulkActionVariables,
  capitalizeIdentifier,
  wrapClientSideError: ErrorWrapper.forClientSideError,
  errorIfDataAbsent: ErrorWrapper.errorIfDataAbsent,
  namespaceDataPath,
  camelize,
  isEqual
};
NudgesmartClient.prototype[$coreImplementation] = coreImplementation;
const Client = NudgesmartClient;
export {
  Client,
  DefaultAiInsightSelection2 as DefaultAiInsightSelection,
  DefaultAiUsageDailySelection2 as DefaultAiUsageDailySelection,
  DefaultAnalyticsConfigurationSelection2 as DefaultAnalyticsConfigurationSelection,
  DefaultCampaignEmailSettingsSelection2 as DefaultCampaignEmailSettingsSelection,
  DefaultCampaignEventsSelection2 as DefaultCampaignEventsSelection,
  DefaultCampaignRulesSelection2 as DefaultCampaignRulesSelection,
  DefaultCampaignSelection2 as DefaultCampaignSelection,
  DefaultCampaignTemplateSelection2 as DefaultCampaignTemplateSelection,
  DefaultCustomThemeSelection2 as DefaultCustomThemeSelection,
  DefaultEmailCaptureSelection2 as DefaultEmailCaptureSelection,
  DefaultEmailLogSelection2 as DefaultEmailLogSelection,
  DefaultEmailProviderConfigurationSelection2 as DefaultEmailProviderConfigurationSelection,
  DefaultEmailTemplateSelection2 as DefaultEmailTemplateSelection,
  DefaultExperimentAnalyticsSelection2 as DefaultExperimentAnalyticsSelection,
  DefaultExperimentAssignmentSelection2 as DefaultExperimentAssignmentSelection,
  DefaultExperimentResultSelection2 as DefaultExperimentResultSelection,
  DefaultExperimentSegmentSelection2 as DefaultExperimentSegmentSelection,
  DefaultExperimentSelection2 as DefaultExperimentSelection,
  DefaultExperimentVariantSelection2 as DefaultExperimentVariantSelection,
  DefaultGdprExportSelection2 as DefaultGdprExportSelection,
  DefaultHolidayCacheSelection2 as DefaultHolidayCacheSelection,
  DefaultIntentWeightConfigSelection2 as DefaultIntentWeightConfigSelection,
  DefaultKlaviyoAudienceMembershipSelection2 as DefaultKlaviyoAudienceMembershipSelection,
  DefaultPartnerReferralSelection2 as DefaultPartnerReferralSelection,
  DefaultPlanSelection2 as DefaultPlanSelection,
  DefaultRulesTemplateSelection2 as DefaultRulesTemplateSelection,
  DefaultSessionSelection2 as DefaultSessionSelection,
  DefaultShopNotificationsSelection2 as DefaultShopNotificationsSelection,
  DefaultShopPropertyKeySelection2 as DefaultShopPropertyKeySelection,
  DefaultShopifyAppSelection2 as DefaultShopifyAppSelection,
  DefaultShopifyAppSubscriptionSelection2 as DefaultShopifyAppSubscriptionSelection,
  DefaultShopifyCustomerAddressSelection2 as DefaultShopifyCustomerAddressSelection,
  DefaultShopifyCustomerSelection2 as DefaultShopifyCustomerSelection,
  DefaultShopifyDiscountCodeSelection2 as DefaultShopifyDiscountCodeSelection,
  DefaultShopifyFileSelection2 as DefaultShopifyFileSelection,
  DefaultShopifyGdprRequestSelection2 as DefaultShopifyGdprRequestSelection,
  DefaultShopifyOrderSelection2 as DefaultShopifyOrderSelection,
  DefaultShopifyPriceRuleSelection2 as DefaultShopifyPriceRuleSelection,
  DefaultShopifyProductMediaSelection2 as DefaultShopifyProductMediaSelection,
  DefaultShopifyProductSelection2 as DefaultShopifyProductSelection,
  DefaultShopifyShopSelection2 as DefaultShopifyShopSelection,
  DefaultShopifySyncSelection2 as DefaultShopifySyncSelection,
  DefaultSpinwheelResultSelection2 as DefaultSpinwheelResultSelection,
  DefaultSurveyResponseSelection2 as DefaultSurveyResponseSelection,
  DefaultTargetingRuleSelection2 as DefaultTargetingRuleSelection,
  DefaultTemplateAssetSelection2 as DefaultTemplateAssetSelection,
  NudgesmartClient,
  maybeGetAuthenticationModeOptionsFromClientOptions
};
//# sourceMappingURL=Client.js.map
